import React, { useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../context/themeContext';
import { Button } from './voiceUi';
import { handleApiError } from '../utils/errorHandler';
import { getLimitHit, onLimitHit, setLimitHit, type LimitHit } from '../utils/limitHit';
import { openTopUp, openUpgrade, type BillingTrigger } from '../utils/skBilling';

/**
 * THE PLAN-LIMIT BANNER — Voice's only own billing UI (CC-05 §2).
 *
 * Shown when the API refuses something the plan does not cover. Two buttons and no
 * amounts: Upgrade and Top up open Platform's drawer with the reason. Either is
 * hidden when the refusal's `upgradeHint` says it cannot help.
 */
function triggerOf(hit: LimitHit): BillingTrigger {
  if (hit.featureCode) return { kind: 'feature_locked', featureCode: hit.featureCode };
  if (hit.unitCode) return { kind: 'limit_hit', unitCode: hit.unitCode };
  return { kind: 'manual' };
}

export default function LimitHitBanner() {
  const { theme } = useTheme();
  const [hit, setHit] = useState<LimitHit | null>(getLimitHit());
  useEffect(() => onLimitHit(setHit), []);
  if (!hit) return null;

  const open = (fn: (t: BillingTrigger) => Promise<void>) => () => {
    setLimitHit(null);
    fn(triggerOf(hit)).catch(err => handleApiError(err, 'The plan could not be opened'));
  };
  // A locked feature is bought by upgrading; a top-up never unlocks one.
  const canTopUp = hit.hint.canTopUp && !hit.featureCode;

  return (
    <View
      accessibilityRole="alert"
      style={{
        flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        paddingHorizontal: 24, paddingVertical: 10, backgroundColor: '#FFF7E6', borderBottomWidth: 1, borderBottomColor: '#F2D9A6',
      }}
    >
      <Text style={{ flexShrink: 1, color: '#5A3E00', fontSize: 13, fontWeight: '600', fontFamily: theme.font.sans }}>
        {hit.featureCode ? 'Your plan does not include this.' : 'You have reached a limit of your plan.'}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {hit.hint.canUpgrade ? <Button kind="primary" label="Upgrade" onPress={open(openUpgrade)} /> : null}
        {canTopUp ? <Button kind="default" label="Top up" onPress={open(openTopUp)} /> : null}
        <Button kind="ghost" label="Dismiss" onPress={() => setLimitHit(null)} />
      </View>
    </View>
  );
}
